import {
    AlertTriangle,
    ArrowRight,
    Check,
    Ghost,
    Loader2,
    MapPin,
    Trash2,
    Users,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import BONavigation from "../../../components/admin/BONavigation";
import {
    GhostCandidate,
    GhostCluster,
    GhostRecord,
    deleteGhost,
    fetchGhostQueue,
    keepGhost,
    mergeGhost,
} from "../../../interfaces/ghostReview";

// ─────────────────────────────────────────────────────────────────────────────
// Small presentational helpers
// ─────────────────────────────────────────────────────────────────────────────

const fmtDate = (d: string | null) => (d ? new Date(d).toLocaleDateString("fr-FR") : "—");

function Field({ label, value, strong }: { label: string; value: React.ReactNode; strong?: boolean }) {
    return (
        <div className="flex items-baseline gap-2">
            <span className="w-24 shrink-0 text-[11px] text-gray-400">{label}</span>
            <span className={`text-xs ${strong ? "font-medium text-gray-900" : "text-gray-700"} break-words`}>
                {value || <span className="text-gray-300">—</span>}
            </span>
        </div>
    );
}

function StatusPill({ record }: { record: GhostRecord }) {
    const closed = record.status === "closed" || !record.isActive;
    return (
        <span
            className={`inline-block rounded-full border px-2 py-0.5 text-[10px] font-medium ${
                closed
                    ? "border-gray-200 bg-gray-50 text-gray-600"
                    : "border-green-200 bg-green-50 text-green-700"
            }`}
        >
            {closed ? "Fermé" : "Actif"}
        </span>
    );
}

function EnrichmentChips({ record }: { record: GhostRecord }) {
    if (!record.event) {
        return <p className="text-[11px] text-gray-400">Aucun event lié</p>;
    }
    const { enrichments, approvedCount } = record.event;
    return (
        <div>
            <p className="text-[11px] text-gray-500 mb-1">
                Event · <strong className="text-gray-700">{approvedCount}</strong> approuvé(s) sur{" "}
                {enrichments.length}
            </p>
            <div className="flex flex-wrap gap-1">
                {enrichments.map((e, i) => (
                    <span
                        key={`${e.type}-${i}`}
                        title={`${e.type} — ${e.status}${e.hasData ? " (données)" : ""}`}
                        className={`inline-block rounded-full border px-1.5 py-0.5 text-[10px] ${
                            e.status === "approved"
                                ? "border-green-200 bg-green-50 text-green-700"
                                : e.status === "closed"
                                ? "border-gray-200 bg-gray-50 text-gray-400"
                                : "border-gray-200 bg-white text-gray-500"
                        }`}
                    >
                        {e.type}
                        {e.hasData && <span className="ml-0.5 text-[8px]">●</span>}
                    </span>
                ))}
            </div>
        </div>
    );
}

/** Why a human might pick this candidate. */
function MatchBadges({ candidate }: { candidate: GhostCandidate }) {
    const { nameSimilarity, sameCity, samePostcode, sharedPeople } = candidate.match;
    const simColor =
        nameSimilarity >= 0.9
            ? "border-green-200 bg-green-50 text-green-700"
            : nameSimilarity >= 0.7
            ? "border-amber-200 bg-amber-50 text-amber-700"
            : "border-gray-200 bg-gray-50 text-gray-500";

    return (
        <div className="flex flex-wrap items-center gap-1.5">
            <span className={`inline-block rounded-full border px-2 py-0.5 text-[10px] font-medium ${simColor}`}>
                Nom {(nameSimilarity * 100).toFixed(0)}%
            </span>
            {sameCity && (
                <span className="inline-flex items-center gap-1 rounded-full border border-green-200 bg-green-50 px-2 py-0.5 text-[10px] text-green-700">
                    <MapPin className="h-2.5 w-2.5" /> Même ville
                </span>
            )}
            {samePostcode && (
                <span className="inline-block rounded-full border border-green-200 bg-green-50 px-2 py-0.5 text-[10px] text-green-700">
                    Même CP
                </span>
            )}
            {sharedPeople > 0 && (
                <span className="inline-flex items-center gap-1 rounded-full border border-violet-200 bg-violet-50 px-2 py-0.5 text-[10px] text-violet-700">
                    <Users className="h-2.5 w-2.5" /> {sharedPeople} contact(s) en commun
                </span>
            )}
        </div>
    );
}

function RecordBody({ record }: { record: GhostRecord }) {
    const addr = [record.address.street, record.address.postcode, record.address.city]
        .filter(Boolean)
        .join(", ");
    return (
        <div className="space-y-1">
            <Field label="SIRET" value={record.siret ? <span className="font-mono">{record.siret}</span> : ""} strong />
            <Field label="Raison soc." value={record.legalName} />
            <Field label="Adresse" value={addr} />
            <Field label="NAF" value={record.nafCode ? <span className="font-mono">{record.nafCode}</span> : ""} />
            <Field label="Contact" value={[record.phone, record.email, record.website].filter(Boolean).join(" · ")} />
            <Field label="Ouverture" value={fmtDate(record.openingDate)} />
            <Field label="Fermeture" value={fmtDate(record.closingDate)} />
            <Field label="Créé le" value={fmtDate(record.createdAt)} />
            <Field
                label="Contacts"
                value={record.peopleCount > 0 ? `${record.peopleCount} personne(s)` : ""}
            />
            <Field
                label="Signaux"
                value={
                    record.signals.length > 0 ? (
                        <span className="flex flex-wrap gap-1">
                            {record.signals.map((s, i) => (
                                <span
                                    key={i}
                                    className="inline-block rounded-full border border-gray-200 bg-white px-1.5 py-0.5 text-[10px] text-gray-600"
                                >
                                    {s.type}
                                    {s.date ? ` · ${new Date(s.date).getFullYear()}` : ""}
                                </span>
                            ))}
                        </span>
                    ) : (
                        ""
                    )
                }
            />
            <div className="pt-1">
                <EnrichmentChips record={record} />
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// One ambiguous cluster
// ─────────────────────────────────────────────────────────────────────────────

function ClusterCard({
    cluster,
    onResolved,
}: {
    cluster: GhostCluster;
    onResolved: (siren: string, message: string) => void;
}) {
    const [busy, setBusy] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    const run = async (label: string, fn: () => Promise<string>) => {
        setBusy(label);
        setError(null);
        try {
            onResolved(cluster.siren, await fn());
        } catch (e: any) {
            setError(e?.message || "Échec");
            setBusy(null);
        }
    };

    return (
        <div className="bg-white border border-gray-200 rounded-xs mb-6">
            {/* Header */}
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-200 px-4 py-2.5">
                <div className="flex items-center gap-2">
                    <Ghost className="h-4 w-4 text-amber-600" />
                    <span className="text-xs font-semibold text-gray-900">SIREN {cluster.siren}</span>
                    <span className="text-[11px] text-gray-500">
                        {cluster.candidates.length} établissement(s) avec SIRET
                    </span>
                </div>
                <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700">
                    Meilleure similarité {(cluster.bestScore * 100).toFixed(0)}% — sous le seuil de 90%
                </span>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,340px)_1fr]">
                {/* Ghost */}
                <div className="border-b lg:border-b-0 lg:border-r border-gray-200 p-4 bg-[#FFFDF8]">
                    <div className="mb-2 flex items-center gap-2">
                        <span className="rounded-full border border-amber-300 bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800">
                            SANS SIRET
                        </span>
                        <StatusPill record={cluster.ghost} />
                    </div>
                    <h3 className="mb-2 text-sm font-semibold text-gray-900 break-words">
                        {cluster.ghost.name || <span className="text-gray-400">Sans nom</span>}
                    </h3>
                    <RecordBody record={cluster.ghost} />

                    <div className="mt-4 flex flex-col gap-2 border-t border-gray-200 pt-3">
                        <button
                            onClick={() =>
                                run("keep", async () => {
                                    await keepGhost(cluster.ghost.id);
                                    return "Conservé comme établissement distinct";
                                })
                            }
                            disabled={!!busy}
                            className="w-full rounded-xs border border-gray-200 bg-white px-3 py-2 text-xs font-medium text-gray-700 hover:bg-[#F9F6F3] disabled:opacity-40"
                        >
                            {busy === "keep" ? "…" : "Garder séparé"}
                        </button>
                        <button
                            onClick={() =>
                                run("delete", async () => {
                                    await deleteGhost(cluster.ghost.id);
                                    return "Fantôme supprimé";
                                })
                            }
                            disabled={!!busy}
                            className="flex w-full items-center justify-center gap-1.5 rounded-xs border border-red-200 bg-white px-3 py-2 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-40"
                        >
                            <Trash2 className="h-3.5 w-3.5" />
                            {busy === "delete" ? "…" : "Supprimer"}
                        </button>
                        {error && (
                            <p className="flex items-start gap-1 text-[11px] text-red-600">
                                <AlertTriangle className="h-3 w-3 shrink-0 mt-0.5" />
                                {error}
                            </p>
                        )}
                    </div>
                </div>

                {/* Candidates */}
                <div className="p-4">
                    <p className="mb-3 text-[11px] text-gray-500">
                        Choisissez l'établissement dans lequel fusionner. Les données du fantôme
                        (contacts, signaux, adresse, enrichissements approuvés) y seront reportées
                        sans écraser l'existant, puis le fantôme sera supprimé.
                    </p>
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
                        {cluster.candidates.map((c) => (
                            <div key={c.id} className="rounded-xs border border-gray-200 p-3 hover:border-gray-300">
                                <div className="mb-1.5 flex items-start justify-between gap-2">
                                    <h4 className="text-xs font-semibold text-gray-900 break-words">
                                        {c.name || <span className="text-gray-400">Sans nom</span>}
                                    </h4>
                                    <StatusPill record={c} />
                                </div>
                                <div className="mb-2">
                                    <MatchBadges candidate={c} />
                                </div>
                                <RecordBody record={c} />
                                <button
                                    onClick={() =>
                                        run(`merge-${c.id}`, async () => {
                                            const { carries } = await mergeGhost(cluster.ghost.id, c.id);
                                            return `Fusionné dans « ${c.name} »${
                                                carries.length ? ` — ${carries.join(", ")}` : ""
                                            }`;
                                        })
                                    }
                                    disabled={!!busy}
                                    className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-xs bg-black px-3 py-2 text-xs font-semibold text-white hover:bg-gray-900 disabled:cursor-not-allowed disabled:bg-gray-300"
                                >
                                    {busy === `merge-${c.id}` ? (
                                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                    ) : (
                                        <ArrowRight className="h-3.5 w-3.5" />
                                    )}
                                    Fusionner ici
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────────────────────

export default function GhostReview() {
    const [clusters, setClusters] = useState<GhostCluster[]>([]);
    const [cursor, setCursor] = useState<string | null>(null);
    const [maxItems, setMaxItems] = useState(0);
    const [scanned, setScanned] = useState(0);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [toast, setToast] = useState<string | null>(null);
    const [resolvedCount, setResolvedCount] = useState(0);

    /**
     * The queue is a cursor, not a page number: the server scans SIRENs in order and hands
     * back where it stopped. A window that yields nothing is not the end of the queue —
     * ambiguous ghosts are rare and can sit far into the base — so keep pulling until
     * something turns up or the scan runs out. Stopping on the first empty window is what
     * left "0 cas à arbitrer" on screen with no way to go further.
     */
    const scan = useCallback(async (from: string | null, append: boolean) => {
        if (append) setLoadingMore(true);
        else {
            setLoading(true);
            setClusters([]);
            setScanned(0);
        }
        setError(null);
        try {
            let next = from;
            let found = 0;
            do {
                const requested = next;
                const data = await fetchGhostQueue(requested);
                // A cursor that hasn't moved would make this loop hammer the same window
                // forever; treat it as the end instead.
                next = data.nextAfter === requested ? null : data.nextAfter;
                found += data.items.length;
                setMaxItems(data.maxItems);
                setScanned((n) => n + data.sirensInspected);
                if (data.items.length > 0) setClusters((prev) => [...prev, ...data.items]);
            } while (found === 0 && next);
            setCursor(next);
        } catch (e: any) {
            setError(e?.message || "Chargement impossible");
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    }, []);

    useEffect(() => {
        scan(null, false);
    }, [scan]);

    const onResolved = (siren: string, message: string) => {
        setClusters((prev) => prev.filter((c) => c.siren !== siren));
        setResolvedCount((n) => n + 1);
        setToast(message);
        window.setTimeout(() => setToast(null), 4000);
    };

    return (
        <div className="min-h-screen bg-[#F5F0EB]">
            <div className="mx-auto max-w-7xl px-4 pb-16">
                <div className="mb-6 pt-6">
                    <div className="flex items-center gap-2">
                        <Ghost className="h-5 w-5 text-gray-600" />
                        <div>
                            <h1 className="text-xl font-semibold text-black">Fantômes à arbitrer</h1>
                            <p className="mt-0.5 text-xs text-gray-600">
                                Établissements sans SIRET que la migration n'a pas su rattacher :
                                plusieurs candidats sur le même SIREN, rien pour les départager, et
                                des enrichissements approuvés à ne pas perdre
                            </p>
                        </div>
                    </div>
                </div>

                <BONavigation />

                {/* Summary */}
                <div className="mb-6 flex flex-wrap items-center gap-3">
                    <span className="rounded-xs border border-gray-200 bg-white px-3 py-1.5 text-xs text-gray-600">
                        <strong className="text-gray-900">{clusters.length}</strong> cas à arbitrer
                    </span>
                    <span className="rounded-xs border border-gray-200 bg-white px-3 py-1.5 text-xs text-gray-600">
                        {scanned.toLocaleString("fr-FR")} SIREN avec fantôme analysés
                    </span>
                    {resolvedCount > 0 && (
                        <span className="flex items-center gap-1.5 rounded-xs border border-green-200 bg-green-50 px-3 py-1.5 text-xs text-green-700">
                            <Check className="h-3.5 w-3.5" />
                            {resolvedCount} traité(s) dans cette session
                        </span>
                    )}
                </div>

                {toast && (
                    <div className="mb-4 flex items-center gap-2 rounded-xs border border-green-200 bg-green-50 p-3">
                        <Check className="h-4 w-4 shrink-0 text-green-600" />
                        <p className="text-xs text-green-800">{toast}</p>
                    </div>
                )}

                {error && (
                    <div className="mb-4 flex items-start gap-2 rounded-xs border border-red-200 bg-white p-4">
                        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-red-600" />
                        <p className="text-xs text-red-600">{error}</p>
                    </div>
                )}

                {clusters.map((c) => (
                    <ClusterCard key={c.siren + c.ghost.id} cluster={c} onResolved={onResolved} />
                ))}

                {loading && (
                    <div className="flex items-center justify-center gap-2 py-10 text-xs text-gray-500">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Analyse des SIREN portant un fantôme…
                        {scanned > 0 && ` ${scanned.toLocaleString("fr-FR")} parcourus`}
                    </div>
                )}

                {!loading && clusters.length === 0 && !error && !cursor && (
                    <div className="rounded-xs border border-gray-200 bg-white p-10 text-center">
                        <Ghost className="mx-auto mb-3 h-8 w-8 text-gray-300" />
                        <p className="text-sm font-medium text-gray-900">Aucun cas à arbitrer</p>
                        <p className="mt-1 text-xs text-gray-500">
                            Base entièrement analysée : la migration a su rattacher ou supprimer
                            tous les fantômes.
                        </p>
                    </div>
                )}

                {cursor && !loading && (
                    <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xs border border-gray-200 bg-white p-3">
                        <p className="text-[11px] text-gray-600">
                            Analyse arrêtée à {scanned.toLocaleString("fr-FR")} SIREN (paliers de{" "}
                            {maxItems.toLocaleString("fr-FR")} cas) — la base n'est pas épuisée.
                        </p>
                        <button
                            onClick={() => scan(cursor, true)}
                            disabled={loadingMore}
                            className="flex items-center gap-1.5 rounded-xs bg-black px-3 py-1.5 text-xs text-white disabled:opacity-50"
                        >
                            {loadingMore && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                            {loadingMore ? "Analyse…" : "Continuer l'analyse"}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
